import React, { useState, useEffect } from 'react';
import { Movie, Screening } from '../types';
import { supabase } from '../lib/supabase';
import BookingModal from './BookingModal';
import { MovieCard } from './ui/MovieCard';

interface MovieListProps {
  movies: Movie[];
  session: any;
  onViewDetails?: (id: number) => void;
  onBookMovie?: (movie: Movie) => void;
}


export default function MovieList({ movies, session, onViewDetails, onBookMovie }: MovieListProps) {
  const [selectedMovie, setSelectedMovie] = React.useState<Movie | null>(null);
  const [screenings, setScreenings] = React.useState<Screening[]>([]);
  const [showBooking, setShowBooking] = useState(false);
  const [closedByMovie, setClosedByMovie] = useState<Record<number, { closed: boolean; soldOut: boolean; screenings: Screening[] }>>({});

  useEffect(() => {
    (async () => {
      const ids = movies.map(m => m.id)
      if (!ids.length) return
      const { data: sc } = await supabase.from('screenings').select('*').in('movie_id', ids)
      const screeningsByMovie: Record<number, Screening[]> = {}
      for (const s of ((sc || []) as Screening[])) {
        if (!screeningsByMovie[s.movie_id]) screeningsByMovie[s.movie_id] = []
        screeningsByMovie[s.movie_id].push(s)
      }
      const allScreeningIds = ((sc || []) as Screening[]).map(s => s.id)
      const { data: layouts } = await supabase.from('seat_layouts').select('*').in('screening_id', allScreeningIds)
      const layoutsBySid: Record<number, { rows: number; cols: number; seats: boolean[][] }> = {}
      for (const l of ((layouts || []) as any[])) layoutsBySid[l.screening_id] = { rows: l.rows, cols: l.cols, seats: l.seats }
      const { data: bs } = await supabase.from('booking_seats').select('screening_id, seat_ids, seat_id').in('screening_id', allScreeningIds)
      const { data: splitRows } = await supabase.from('seatssplitting').select('*').in('screening_id', allScreeningIds)
      const splitBySid: Record<number, any> = {}
      for (const r of ((splitRows || []) as any[])) splitBySid[r.screening_id] = r
      const { data: simpleB } = await supabase.from('bookings').select('screening_id,status,seats').in('screening_id', allScreeningIds)
      const simpleBySid: Record<number, number> = {}
      for (const r of ((simpleB || []) as any[])) { if ((r.status || '') === 'confirmed') simpleBySid[r.screening_id] = (simpleBySid[r.screening_id] || 0) + Number(r.seats || 0) }
      const calcCapacity = (sid: number, s: Screening) => {
        const l = layoutsBySid[sid]
        if (l) {
          let count = 0
          for (let r = 0; r < l.rows; r++) for (let c = 0; c < l.cols; c++) if (l.seats[r][c]) count++
          return count
        }
        const sp = splitBySid[sid]
        if (sp) return sp.silver_total + sp.gold_total + sp.diamond_total
        return (s.available_seats || 0) + (simpleBySid[sid] || 0)
      }
      const bookedForSid = (sid: number) => {
        const bsForSid = ((bs || []) as any[]).filter(x => Number(x.screening_id) === sid)
        const byLayout = bsForSid.reduce((acc, row) => acc + (Array.isArray(row.seat_ids) ? (row.seat_ids as any[]).length : 1), 0)
        if (byLayout > 0) return byLayout
        const sp = splitBySid[sid]
        if (sp) return sp.silver_booked + sp.gold_booked + sp.diamond_booked
        return simpleBySid[sid] || 0
      }
      const result: Record<number, { closed: boolean; soldOut: boolean; screenings: Screening[] }> = {}
      const now = new Date()
      for (const m of movies) {
        const scs = screeningsByMovie[m.id] || []
        const capacity = scs.reduce((acc, s) => acc + calcCapacity(s.id, s), 0)
        const booked = scs.reduce((acc, s) => acc + bookedForSid(s.id), 0)
        const remaining = Math.max(0, capacity - booked)
        const startOk = m.booking_start_time ? now >= new Date(m.booking_start_time) : true
        const stopOk = m.booking_stop_enabled ? (!!m.booking_stop_time && now < new Date(m.booking_stop_time)) : true
        const closed = !m.accept_booking || !startOk || !stopOk || remaining === 0
        result[m.id] = { closed, soldOut: remaining === 0, screenings: scs }
      }
      setClosedByMovie(result)
    })()
  }, [movies])

  const handleMovieClick = async (movie: Movie) => {
    if (onBookMovie) {
      onBookMovie(movie);
      return;
    }
    const state = closedByMovie[movie.id];
    if (state?.closed) {
      alert('sorry. the booking is over');
      return;
    }
    const { data, error } = await supabase
      .from('screenings')
      .select('*')
      .eq('movie_id', movie.id)
      .gte('start_time', new Date().toISOString())
      .order('start_time', { ascending: true });
    if (!error) {
      setScreenings(data || []);
      setSelectedMovie(movie);
      setShowBooking(true);
    }
  };

  const handleViewMore = (id: number) => {
    if (onViewDetails) onViewDetails(id);
  };


  return (
    <div>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {movies.map((movie) => {
          const movieForCard: Movie = {
            ...movie,
            genre: movie.genre ?? 'movie',
            rating: movie.rating ?? 9.5,
            backdropUrl: movie.backdropUrl ?? movie.image_url,
          };
          const screenings = closedByMovie[movie.id]?.screenings || [];
          const now = new Date();
          const hasScreenings = screenings.length > 0;
          // An event is "Ended" if it had screenings, but ALL of them are in the past.
          const allPast = hasScreenings && screenings.every(s => new Date(s.start_time) < now);

          return (
            <MovieCard
              key={movie.id}
              movie={movieForCard}
              eventEnded={allPast}
              onBook={() => handleMovieClick(movie)}
              onViewMore={handleViewMore}
            />
          );
        })}
      </div>

      {!onBookMovie && selectedMovie && (
        <BookingModal
          show={showBooking}
          onClose={() => setShowBooking(false)}
          movie={selectedMovie}
          screenings={screenings}
          session={session}
        />
      )}
    </div>
  );
}
